import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, CircularProgress,
  Dialog, DialogContent, DialogActions, TextField, Grid, FormControl,
  InputLabel, Select, MenuItem, Autocomplete, Chip, Alert,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Close as CloseIcon, Inventory as InventoryIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { ownershipShortLabel } from '../pages/Inventory/inventoryConstants';
import {
  PROCEDURE_QUANTITY_UNIT_OPTIONS,
  type ProcedureQuantityUnit,
  resolveMaterialQuantity,
  vialTotalMgFromItemMetadata,
} from '../constants/inventoryConsumption';

type Props = {
  planItemId: string;
  patientId?: string;
  treatmentPlanId?: string;
  procedureId?: string;
  reservations?: any[];
  onChanged?: () => void;
};

function reservationQuantityLabel(r: any): string {
  const item = r.clinic_inventory_items;
  const meta = r.metadata || {};
  if (meta.quantity_unit === 'mg' && meta.dose_mg != null) {
    const stockUnit = item?.unit || 'un';
    return `${meta.dose_mg} mg (≈ ${r.quantity} ${stockUnit} de estoque)`;
  }
  return `${r.quantity} ${item?.unit || 'un'}`;
}

const InventoryPlanItemReservations: React.FC<Props> = ({
  planItemId,
  patientId,
  treatmentPlanId,
  procedureId,
  reservations: initialReservations = [],
  onChanged,
}) => {
  const [entries, setEntries] = useState<any[]>(initialReservations);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [allItems, setAllItems] = useState<any[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [batches, setBatches] = useState<any[]>([]);
  const [batchId, setBatchId] = useState('');
  const [quantityUnit, setQuantityUnit] = useState<ProcedureQuantityUnit>('item_unit');
  const [quantity, setQuantity] = useState<number | ''>(1);
  const [notes, setNotes] = useState('');
  const [availability, setAvailability] = useState<any | null>(null);

  useEffect(() => {
    setEntries(initialReservations);
  }, [initialReservations]);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`/inventory-module/reservations/plan-item/${planItemId}`));
      setEntries((res.data.data || []).filter((r: any) => r.status === 'active'));
    } catch {
      setEntries(initialReservations);
    } finally {
      setLoading(false);
    }
  }, [planItemId, initialReservations]);

  useEffect(() => {
    if (!dialogOpen) return;
    setItemsLoading(true);
    axios.get(getApiUrl('/inventory-module/items'), { params: { limit: 500, page: 1 } })
      .then((res) => setAllItems(res.data.data || []))
      .catch(() => setAllItems([]))
      .finally(() => setItemsLoading(false));
  }, [dialogOpen]);

  const itemOptions = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    const pool = allItems.filter((i) => i.is_active !== false);
    if (!q) return pool.slice(0, 80);
    return pool.filter((i) => {
      const hay = [
        i.name,
        i.sku,
        i.barcode,
        i.active_ingredient,
        i.item_type,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }).slice(0, 80);
  }, [allItems, itemSearch]);

  useEffect(() => {
    if (!selectedItem?.id) {
      setBatches([]);
      setBatchId('');
      setAvailability(null);
      return;
    }
    axios.get(getApiUrl(`/inventory-module/items/${selectedItem.id}/positions`))
      .then((res) => setBatches(res.data.data || []))
      .catch(() => setBatches([]));
    axios.get(getApiUrl(`/inventory-module/items/${selectedItem.id}/availability`), {
      params: batchId ? { batch_id: batchId } : undefined,
    })
      .then((res) => setAvailability(res.data.data))
      .catch(() => setAvailability(null));
  }, [selectedItem?.id, batchId]);

  const vialTotalMg = selectedItem ? vialTotalMgFromItemMetadata(selectedItem.metadata) : null;

  const stockQuantityPreview = useMemo(() => {
    const raw = quantity === '' ? 0 : Number(quantity);
    if (!raw || raw <= 0 || !selectedItem) return null;
    if (quantityUnit === 'mg') {
      return resolveMaterialQuantity({
        quantity_per_execution: raw,
        quantity_unit: 'mg',
        itemMetadata: selectedItem.metadata,
      });
    }
    return raw;
  }, [quantity, quantityUnit, selectedItem]);

  const openDialog = () => {
    setError('');
    setSelectedItem(null);
    setBatchId('');
    setQuantityUnit('item_unit');
    setQuantity(1);
    setNotes('');
    setItemSearch('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedItem?.id) {
      setError('Selecione o produto ou material');
      return;
    }
    const rawQty = quantity === '' ? 0 : Number(quantity);
    if (!rawQty || rawQty <= 0) {
      setError(quantityUnit === 'mg' ? 'Informe a dose em mg' : 'Informe a quantidade');
      return;
    }
    if (quantityUnit === 'mg' && !vialTotalMg) {
      setError(
        'Este item não tem mg total do frasco cadastrado (metadata vial_total_mg ou content_quantity, ex.: "60 mg"). '
        + 'Ajuste o cadastro do estoque ou use unidade do item.',
      );
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload: Record<string, unknown> = {
        item_id: selectedItem.id,
        batch_id: batchId || null,
        quantity_unit: quantityUnit,
        reference_type: 'treatment_plan_item',
        reference_id: planItemId,
        patient_id: patientId || null,
        treatment_plan_id: treatmentPlanId || null,
        procedure_id: procedureId || null,
        notes: notes || null,
        metadata: { source: 'manual_plan_item' },
      };
      if (quantityUnit === 'mg') {
        payload.dose_mg = rawQty;
        payload.quantity = stockQuantityPreview ?? rawQty;
      } else {
        payload.quantity = rawQty;
      }

      await axios.post(getApiUrl('/inventory-module/reservations'), payload);
      setDialogOpen(false);
      await fetchReservations();
      onChanged?.();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao reservar');
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async (id: string) => {
    if (!window.confirm('Liberar esta reserva? A quantidade volta ao disponível.')) return;
    try {
      await axios.delete(getApiUrl(`/inventory-module/reservations/${id}`));
      await fetchReservations();
      onChanged?.();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erro ao liberar');
    }
  };

  const attrSummary = (item: any) => {
    const attrs = item?.attributes;
    if (!attrs || typeof attrs !== 'object') return null;
    const keys = Object.keys(attrs).slice(0, 3);
    if (!keys.length) return null;
    return keys.map((k) => `${k}: ${attrs[k]}`).join(' · ');
  };

  const availPool = Number(availability?.batch_available ?? availability?.available ?? 0);
  const remainingAfter = stockQuantityPreview != null ? availPool - stockQuantityPreview : null;

  return (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <InventoryIcon sx={{ fontSize: 14 }} /> Reservas de estoque
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={openDialog}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, minWidth: 0, py: 0.25 }}>
          Reservar
        </Button>
      </Box>

      {loading ? (
        <CircularProgress size={16} sx={{ color: '#64748b' }} />
      ) : entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Nenhuma reserva. Ao liberar o procedimento, materiais do cadastro são reservados automaticamente (se houver estoque).
        </Typography>
      ) : (
        <StackList entries={entries} onRelease={handleRelease} attrSummary={attrSummary} />
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e2e8f0' }}>
          <InventoryIcon sx={{ color: '#0D9488' }} />
          <Typography sx={{ flex: 1, fontWeight: 700 }}>Reservar material</Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={itemOptions}
                loading={itemsLoading}
                getOptionLabel={(o) => o.name || ''}
                value={selectedItem}
                onChange={(_, v) => { setSelectedItem(v); setBatchId(''); }}
                onInputChange={(_, v, reason) => { if (reason === 'input') setItemSearch(v); }}
                filterOptions={(x) => x}
                noOptionsText={itemsLoading ? 'Carregando…' : 'Nenhum item encontrado'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Produto / material"
                    size="small"
                    placeholder="Buscar no estoque (nome, SKU, princípio ativo…)"
                    helperText="Lista todos os itens ativos do estoque da unidade"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[option.sku, option.unit, option.item_type].filter(Boolean).join(' · ')}
                      </Typography>
                      {attrSummary(option) && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {attrSummary(option)}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Grid>
            {batches.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Lote (opcional)</InputLabel>
                  <Select value={batchId} label="Lote (opcional)" onChange={(e) => setBatchId(e.target.value)}>
                    <MenuItem value="">Qualquer lote</MenuItem>
                    {batches.map((b) => (
                      <MenuItem key={b.id} value={b.id}>
                        {b.lot_number ? `Lote ${b.lot_number}` : 'Sem lote'}
                        {b.individual_code ? ` · ${b.individual_code}` : ''}
                        {` · ${b.quantity} ${ownershipShortLabel(b.ownership)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de consumo</InputLabel>
                <Select
                  value={quantityUnit}
                  label="Forma de consumo"
                  onChange={(e) => setQuantityUnit(e.target.value as ProcedureQuantityUnit)}
                >
                  {PROCEDURE_QUANTITY_UNIT_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={quantityUnit === 'mg' ? 'Dose (mg)' : 'Quantidade'}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ min: 0.001, step: quantityUnit === 'mg' ? 0.1 : 0.001 }}
                helperText={
                  quantityUnit === 'mg'
                    ? (vialTotalMg ? `Frasco: ${vialTotalMg} mg` : 'Cadastre vial_total_mg no item')
                    : selectedItem?.unit ? `Unidade: ${selectedItem.unit}` : undefined
                }
              />
            </Grid>
            {quantityUnit === 'mg' && selectedItem && (
              <Grid item xs={12}>
                <Alert severity={vialTotalMg ? 'info' : 'warning'} sx={{ py: 0.5 }}>
                  {vialTotalMg ? (
                    <>
                      {stockQuantityPreview != null && quantity !== '' ? (
                        <>
                          Reserva de estoque: ≈ <strong>{stockQuantityPreview}</strong> {selectedItem.unit || 'un'}
                          {' '}(fração do frasco para {quantity} mg).
                        </>
                      ) : (
                        <>Informe os mg aplicados (ex.: 2,5 mg Tirzepatida).</>
                      )}
                    </>
                  ) : (
                    <>Item sem mg total do frasco — use &quot;Unidade do item&quot; ou complete o cadastro do produto.</>
                  )}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              {availability && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Disponível: {availability.available}
                  {availability.batch_available != null && ` · lote: ${availability.batch_available}`}
                  {remainingAfter != null && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        fontWeight: 700,
                        color: remainingAfter < 0 ? '#dc2626' : '#047857',
                      }}
                    >
                      Restante após reserva: {remainingAfter.toFixed(quantityUnit === 'mg' ? 3 : 2)}
                    </Box>
                  )}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observação" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#0D9488', '&:hover': { bgcolor: '#0f766e' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Confirmar reserva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const StackList: React.FC<{
  entries: any[];
  onRelease: (id: string) => void;
  attrSummary: (item: any) => string | null;
}> = ({ entries, onRelease, attrSummary }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {entries.map((r) => {
      const item = r.clinic_inventory_items;
      const batch = r.clinic_inventory_batches;
      return (
        <Box key={r.id} sx={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
          px: 1.25, py: 0.75, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0',
        }}>
          <Box sx={{ minWidth: 0 }}>
            {r.reference_type === 'procedure_ticket' && (
              <Chip
                size="small"
                label={`Reserva futura · sessão ${r.metadata?.session_number || '—'}`}
                sx={{ mb: 0.5, height: 20, bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 700 }}
              />
            )}
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {item?.name || 'Item'} · {reservationQuantityLabel(r)}
            </Typography>
            {attrSummary(item) && (
              <Typography variant="caption" color="text.secondary">{attrSummary(item)}</Typography>
            )}
            {batch && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {batch.lot_number ? `Lote ${batch.lot_number}` : 'Lote s/n'}
                {batch.individual_code ? ` · ${batch.individual_code}` : ''}
                {batch.ownership === 'consigned' ? ' · Consignado' : ''}
              </Typography>
            )}
            {(r.available_after_reservation != null || r.remaining_stock != null) && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: Number(r.available_after_reservation ?? r.remaining_stock) < 0 ? '#dc2626' : '#047857',
                  fontWeight: 700,
                }}
              >
                Estoque livre restante: {r.available_after_reservation ?? r.remaining_stock} {item?.unit || 'un'}
              </Typography>
            )}
            {Number(r.metadata?.shortage_quantity || 0) > 0 && (
              <Typography variant="caption" sx={{ display: 'block', color: '#b45309', fontWeight: 700 }}>
                Cobertura parcial: reservado {r.metadata?.covered_quantity} de {r.metadata?.required_quantity};
                déficit {r.metadata?.shortage_quantity} enviado para compras.
              </Typography>
            )}
          </Box>
          <Tooltip title="Liberar reserva">
            <IconButton size="small" onClick={() => onRelease(r.id)}>
              <DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    })}
  </Box>
);

export default InventoryPlanItemReservations;
